import { View, SxProp, styled } from 'dripsy';
import { Pressable } from 'react-native';

import React from 'react';
import { TextCaption } from '../typography/UniTypography';
import { Moment } from 'moment-timezone';
import { DateRangePickerSelector } from './DateRangePickerSelector';
import { DateRangeProps, useDateRangePicker } from './useDateRangePicker.hook';
import { Icon } from '../icons/Icon';
import { useTranslation } from 'react-i18next';
import { Divider } from '../Divider';

const PressableView = styled(Pressable)();

interface DateRangePickerProps {
  dateRangeProps: DateRangeProps;
  onValueChange: (value: { from?: Date; to?: Date }) => void;
  sx?: SxProp;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({ dateRangeProps, onValueChange, sx }) => {
  const { t } = useTranslation('earn');
  const controller = useDateRangePicker({ dateRangeProps, onValueChange });

  return (
    <View sx={{ ...styles.container, ...sx }}>
      {/* Button Display Date from YYYY-MM-DD to YYYY-MM-DD */}
      <View sx={styles.buttonGroup}>
        <TextCaption semiBold>{t('earn-invest::glossary.date')}</TextCaption>
        <DateButton
          date={controller.value.from}
          onPress={controller.toggle}
          themePrimary={!controller.value.from && !controller.value.to}
        />
        <Icon name="arrowRight" />
        <DateButton
          date={controller.value.to}
          onPress={controller.toggle}
          themePrimary={!!(controller.value.from && !controller.value.to)}
        />
        <Icon name="historyCalendar" />
      </View>
      {/* Selector */}
      <DateRangePickerSelector
        dateRangeProps={dateRangeProps}
        isOpen={controller.isOpen}
        onValueChange={controller.onTempChange}
        onClose={controller.close}
        onConfirm={controller.onConfirmChange}
        onReset={controller.onReset}
      />
    </View>
  );
};

// Date Button
const DateButton: React.FC<{ date: Moment | null; themePrimary?: boolean; sx?: SxProp; onPress: () => void }> = ({
  date,
  themePrimary,
  sx,
  onPress
}) => {
  return (
    <PressableView onPress={onPress} sx={{ ...styles.dateButton, ...sx }}>
      <TextCaption themePrimary={themePrimary}>{date ? date.format('YYYY-MM-DD') : ' - '}</TextCaption>
    </PressableView>
  );
};

enum StyleName {
  container = 'container',
  icon = 'icon',
  buttonGroup = 'buttonGroup',
  dateButton = 'dateButton'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    zIndex: 100
  },
  icon: {
    height: '$5',
    width: '$5'
  },
  buttonGroup: {
    paddingX: '$4',
    paddingY: '$2',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '$borderField',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$3'
  },
  dateButton: {
    minWidth: '$20',
    width: 20,
    alignItems: 'center',
    justifyContent: 'center'
  }
};
